import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModal } from 'apps/auth/actions/appAction';
import { updateFieldValues, updateFormProperties } from 'apps/auth/actions/formAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SelectFromCountryCurrencies from 'apps/auth/components/send/SelectFromCountryCurrenciesScreen';
import { CALCULATOR_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { AMOUNT_FROM, AMOUNT_FROM_CCY, AMOUNT_TO, AMOUNT_TO_CCY, FROM_CNR_ID } from 'apps/auth/constants/fieldsName';
import { AUTH } from 'apps/auth/constants/UrlConstants';
import { getCurrencyIcon, onIconRenderError, toLowerCase } from 'apps/auth/helpers/utils';
import { useAuth, useExtra } from 'apps/auth/hooks/selectors';
import { showPromoModal } from 'apps/auth/services/promoModalService';
import { SendingAmountValidationService } from 'apps/auth/services/sendingAmountValidationService';

import CalculatorCurrenciesPopup from '../../ga/accounts/send/popups/CalculatorCurrenciesPopup';
import CurrenciesPopup from '../../ga/accounts/send/popups/CurrenciesPopup';
import CalculatorScreen from '../../send/multiScreen/CalculatorScreen';
import PayoutStoriesModal from '../modals/PayoutStoriesModal';

const apiFetchDataUrl = '/auth/send/commission.action';
const promoCodeFetchUrl = '/auth/send/promoCode.check';

const getCurrentCurrency = (currencies, amountCcy) => {
    if (Array.isArray(currencies)) {
        return currencies.find((c) => Number(c.id) === Number(amountCcy));
    } else {
        return currencies;
    }
};

const initCurrenciesFrom = (countryListFrom, customCcy) => {
    const currenciesFrom = [];

    countryListFrom?.forEach((country) => {
        country.currencies.forEach((currency) => {
            let ccyId = currency.id;
            if (customCcy) {
                ccyId = customCcy;
            }

            if (currenciesFrom.find((addedCurrency) => addedCurrency.id === ccyId) === undefined) {
                currenciesFrom.push(currency);
            }
        });
    });

    return currenciesFrom;
};

export const SendCalculatorScreenPureComponent = (props) => {
    const [fromCountryFlagName, setFromCountryFlagName] = useState(props.fromCountry);
    const changeCurrencySubmitRef = useRef(null);
    const popupRef = useRef(null);

    const extra = useExtra();
    const auth = useAuth();
    const { leftLimitAmount, profile } = extra;
    const validationService = new SendingAmountValidationService(
        {
            minValidError: props.minValidError,
            youHave: props.youHave,
        },
        leftLimitAmount,
        props.isBonus,
        profile,
        extra,
        auth,
    );

    let currenciesFrom = [];
    if (props.isBonus) {
        currenciesFrom = initCurrenciesFrom(props.countryListFrom, props.amountFromCcy.value);
    } else {
        currenciesFrom = initCurrenciesFrom(props.countryListFrom);
    }

    useEffect(() => {
        if (__isBrowser__) {
            props.sendAmplitudeEventOnRender?.(CALCULATOR_SCREEN_SHOWN);
        }
    }, []);

    useEffect(() => {
        const showModal = showPromoModal(auth.ballId, extra.marketingSliders, 'payout_marketing_info', true);
        if (showModal && props.showCalculatorFirst) {
            props.popupFullwidthRef.current?.open(<PayoutStoriesModal sliders={extra.marketingSliders} popupFullwidthRef={props.popupFullwidthRef} />);
        }
    }, [extra.marketingSliders, props.showCalculatorFirst]);

    const getValidationError = (value, min, max, multiple, isFrom, currencyCode) => {
        return validationService.getValidationError(value, min, max, multiple, isFrom, currencyCode, props.lang, props.country);
    };

    const renderCurrenciesPopup = (country, getHref, ccyField) => {
        return (
            <CurrenciesPopup
                getHref={getHref}
                country={country}
                title={country.countryCurrenciesSelectNotificationTo || props.selectPayCurrencyTitle}
                textCenter
                currencies={country.currencies}
                onCurrencyItemClick={onCurrencyItemClick}
                ccyField={ccyField}
                popupRef={popupRef}
                typeOfTransfer={props.typeOfTransfer}
                getCurrencyIcon={getCurrencyIcon}
                onIconRenderError={onIconRenderError}
            />
        );
    };

    const getHref = (countryCode, currencyCode) => {
        return `${props.sendUrIPrefix}/${toLowerCase(countryCode)}/${toLowerCase(currencyCode)}`;
    };

    const onSelectCurrencyClick = (_e, currencies, amountCcy, ccyField) => {
        const isFrom = ccyField.name === AMOUNT_FROM_CCY;
        if (isFrom) {
            if (props.countryListFrom.length === 1) {
                const country = props.countryListFrom[0];
                popupRef.current?.open(renderCurrenciesPopup(country, getHref, ccyField));
            } else {
                props.toggleModal(
                    true,
                    <SelectFromCountryCurrencies
                        countries={props.countryListFrom}
                        onCountryItemClick={onCountryItemClick}
                        ccyField={ccyField}
                        title={props.sendMoneyFrom}
                        closeModal={() => props.toggleModal(false)}
                        hasCustomSorting
                    />,
                    true,
                    true,
                );
            }
        } else {
            popupRef.current?.open(
                <CalculatorCurrenciesPopup
                    title={props.selectDeliverCurrencyTitle}
                    isBonus={props.isBonus}
                    formName={props.formName}
                    currencies={currencies}
                    current={amountCcy}
                    fetchCurrencyUrl={AUTH}
                    isGlobalFlow={props.isGlobalFlow}
                    popupRef={popupRef}
                    typeOfTransfer={props.typeOfTransfer}
                    textCenter
                    getCurrencyIcon={getCurrencyIcon}
                    onIconRenderError={onIconRenderError}
                />,
            );
        }
    };

    const onCountryItemClick = (_e, country, getHref, ccyField) => {
        if (Array.isArray(country.currencies) && country.currencies.length > 1) {
            popupRef.current?.open(renderCurrenciesPopup(country, getHref, ccyField));
        } else {
            const currency = Array.isArray(country.currencies) && country.currencies.length === 1 ? country.currencies[0] : country.currencies;
            changeCurrency(country, currency);
        }
        setFromCountryFlagName(country.code);
    };

    const onCurrencyItemClick = (_e, country, currency) => {
        changeCurrency(country, currency);
    };

    const getFetchCurrencyUrl = (country, currency) => {
        const countryCode = toLowerCase(country.code);
        const currencyCode = toLowerCase(currency.code);
        let path = `${AUTH}/send/${countryCode}/${currencyCode}/${props.deliveryMethod}?keepAmount=true`;

        if (props.typeOfTransfer) {
            path += `&type=${props.typeOfTransfer}`;
        }

        return path;
    };

    const changeCurrency = (country, currency) => {
        const countryFieldName = FROM_CNR_ID;
        const currencyFieldName = AMOUNT_FROM_CCY;
        const url = getFetchCurrencyUrl(props.countryListTo[0], props.currencyTo);
        props.updateFormProperties(props.formName, url, false).then(() => {
            props
                .updateFieldValues(props.formName, {
                    [countryFieldName]: country.id,
                    [currencyFieldName]: currency.id,
                })
                .then(() => {
                    props.onBeforeCurrencyChange?.();
                    changeCurrencySubmitRef.current.click();
                    props.toggleModal(false);
                });
        });
    };

    const beforeSaveMoneyCodeSubmit = () => {
        return props.updateFormProperties(props.formName, promoCodeFetchUrl, false);
    };

    const onContinueButtonClick = (e) => {
        props.checkFieldsAndSubmitFormIfHasNoErrors(e, [props.amountFrom, props.amountTo]);
    };

    return (
        <>
            <CalculatorScreen
                isSend
                isBonus={props.isBonus}
                isRepeat={props.isRepeat}
                isCanCalcCommission={props.isCanCalcCommission}
                formName={props.formName}
                fetchCommissionUrl={apiFetchDataUrl}
                getValidationError={getValidationError}
                currencyFrom={getCurrentCurrency(currenciesFrom, props.amountFromCcy.value)}
                currencyTo={getCurrentCurrency(props.currenciesTo, props.amountToCcy.value)}
                currenciesFrom={currenciesFrom}
                currenciesTo={props.currenciesTo}
                onSelectCurrencyClick={onSelectCurrencyClick}
                onContinueButtonClick={onContinueButtonClick}
                typeOfTransfer={props.typeOfTransfer}
                promoCodeFetchUrl={promoCodeFetchUrl}
                beforeSaveMoneyCodeSubmit={beforeSaveMoneyCodeSubmit}
                fromCountryFlagName={fromCountryFlagName}
                setDisabledSubmitButton={props.setDisabledSubmitButton}
            />
            <Button type="submit" ref={changeCurrencySubmitRef} variant="hidden" />
            <PopupNew getPopupComponent={(component) => component} ref={popupRef} />
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        minValidError: state.messages.jsMessages['site.js.ga.validation.min.valid.error'],
        formName: ownProps.formName,
        checkFieldsAndSubmitFormIfHasNoErrors: ownProps.checkFieldsAndSubmitFormIfHasNoErrors,
        onBeforeCurrencyChange: ownProps.onBeforeCurrencyChange,
        currenciesTo: state.extra.toCountry?.currencies,
        currencyTo: state.extra.toCurrency,
        currencyFrom: state.extra.fromCurrency,
        countryListFrom: state.extra.countryListFrom,
        countryListTo: [state.extra.toCountry],
        amountFromCcy: state.form[ownProps.formName].fields[AMOUNT_FROM_CCY],
        amountToCcy: state.form[ownProps.formName].fields[AMOUNT_TO_CCY],
        amountFrom: state.form[ownProps.formName].fields[AMOUNT_FROM],
        amountTo: state.form[ownProps.formName].fields[AMOUNT_TO],
        sendMoneyFrom: state.messages.pageMessages.sendMoneyFrom,
        youHave: state.messages.pageMessages.youHave,
        selectPayCurrencyTitle: state.messages.pageMessages.selectPayCurrencyTitle,
        selectDeliverCurrencyTitle: state.messages.pageMessages.selectDeliverCurrencyTitle,
        deliveryMethod: state.extra.deliveryMethod,
        fields: state.form[ownProps.formName].fields,
        submitRef: ownProps.submitRef,
        fromCountry: state.extra.fromCountry?.code,
    };
};

export const SendCalculatorScreen = connect(mapStateToProps, {
    toggleModal,
    updateFormProperties,
    updateFieldValues,
})(SendCalculatorScreenPureComponent);
